import { type QueryResult } from '@apollo/client'
import { useDeepMemo } from '@retailer-platform/shared-common/src/react/react.hooks'
import { isEqual } from 'lodash-es'
import { useEffect, useRef } from 'react'
import { type ApolloError, type QueryHookOptions } from '@apollo/client'
import { ippLocalStorage } from '../local-storage/ippLocalStorage'
import { evictSwrCacheEntriesToBudget } from '../local-storage/swrCacheEviction'

export type QueryHook<TData, TVariables> = (
  // @ts-expect-error TS2344 — strictNullChecks. TODO: fix strict null check
  options?: QueryHookOptions<TData, TVariables>
  // @ts-expect-error TS2344 — strictNullChecks. TODO: fix strict null check
) => QueryResult<TData, TVariables>

/**
 * Helper type to extract TData from a query hook
 */
type ExtractQueryData<T> = T extends (options?: QueryHookOptions<infer TData, any>) => any
  ? TData
  : never

// Cache is namespaced, in case we need to fully clear it without touching other keys
const cacheNamespace = 'staleWhileRevalidate'
// This can be useful to invalidate cache -- recommended to bump if underlying data changes
const cacheVersion = 'v1'
// Maximum size for a single cache entry (200KB) - prevents large responses from filling localStorage
const MAX_CACHE_ENTRY_BYTES = 200 * 1024
// Total budget for the whole SWR namespace (~3MB). The per-entry cap alone does
// not bound growth — one entry accumulates per query-variables permutation across
// every registered op, so without a total budget the namespace can consume the
// entire ~5MB localStorage quota and starve session/auth writes. Kept well under
// quota so critical writes always have headroom.
const SWR_NAMESPACE_BUDGET_BYTES = 3 * 1024 * 1024
// Default maximum age for cache entries (3 days) - entries older than this are treated as expired
const DEFAULT_MAX_CACHE_AGE_MS = 3 * 24 * 60 * 60 * 1000

/**
 * Wrapper type for cached data that includes timestamp for TTL checking
 */
type CachedEntry<TData> = {
  data: TData
  cachedAt: number
}

export type StaleWhileRevalidateOptions = {
  baseCacheKey: string
  /**
   * Maximum age for cache entries in milliseconds.
   * Entries older than this are treated as expired.
   * @default 3 days (259200000ms)
   */
  maxCacheAgeMs?: number
}

/**
 * Reads and parses a cache entry from localStorage.
 * Returns undefined if the key doesn't exist or parsing fails.
 */
const readCache = <T>(key: string): T | undefined => {
  try {
    const raw = localStorage.getItem(key)
    if (raw) return JSON.parse(raw) as T
  } catch (err) {
    console.error('[localStorage] Failed to read/parse cache:', err)
  }
  return undefined
}

/**
 * Serializes and writes a cache entry to localStorage.
 * Silently skips the write if the serialized value exceeds maxBytes.
 */
const writeCache = <T>(key: string, value: T, maxBytes: number): void => {
  try {
    const serialized = JSON.stringify(value)

    if (new Blob([serialized]).size > maxBytes) return

    ippLocalStorage.setItem(key, serialized)

    // Bound total namespace growth off the critical path: a coalesced idle sweep
    // evicts oldest entries back under budget, leaving quota headroom for
    // session/auth writes. Kept off the write itself so the hot path never pays
    // the namespace scan (the per-write cost this replaces was tens of ms/page).
    scheduleBudgetEviction()
  } catch (err) {
    console.error('[localStorage] Failed to write cache:', err)
  }
}

/**
 * Schedules a callback via requestIdleCallback (with setTimeout fallback).
 * Returns a cleanup function that cancels the scheduled callback.
 */
const scheduleIdle = (callback: () => void): (() => void) => {
  if (typeof requestIdleCallback === 'function') {
    const id = requestIdleCallback(callback)
    return () => cancelIdleCallback(id)
  }
  const id = setTimeout(callback, 0)
  return () => clearTimeout(id)
}

// Coalesces budget eviction so a burst of writes on one page load schedules at
// most one idle sweep. Eviction is proactive headroom, not a correctness gate
// for the write itself (ippLocalStorage.setItem drops droppable SWR keys and
// retries on QuotaExceededError), so it runs off the write's critical path.
let budgetEvictionScheduled = false
const scheduleBudgetEviction = (): void => {
  if (budgetEvictionScheduled) return
  budgetEvictionScheduled = true
  scheduleIdle(() => {
    budgetEvictionScheduled = false
    const evicted = evictSwrCacheEntriesToBudget(SWR_NAMESPACE_BUDGET_BYTES)
    // Keep the L1 memory cache consistent with what left localStorage.
    if (evicted > 0) memoryCache.clear()
  })
}

/**
 * Module-level in-memory cache — acts as an L1 cache in front of localStorage.
 * Shared across all hook instances, so multiple components using the same cache
 * key avoid redundant localStorage I/O.
 */
const memoryCache = new Map<string, CachedEntry<any>>()

/** @internal Exposed for testing only — clears the in-memory cache layer. */
export const _resetMemoryCacheForTesting = (): void => {
  memoryCache.clear()
}

/**
 * Reads a cache entry, checking the in-memory Map first and falling through
 * to localStorage only on a cache miss. Populates the Map on a miss so
 * subsequent reads are O(1).
 */
const readCacheEntry = <TData>(key: string): CachedEntry<TData> | undefined => {
  if (memoryCache.has(key)) return memoryCache.get(key) as CachedEntry<TData>
  const entry = readCache<CachedEntry<TData>>(key)
  if (entry) memoryCache.set(key, entry)
  return entry
}

/**
 * Writes a cache entry to both the in-memory Map and localStorage.
 */
const writeCacheEntry = <TData>(key: string, entry: CachedEntry<TData>, maxBytes: number): void => {
  memoryCache.set(key, entry)
  writeCache(key, entry, maxBytes)
}

/**
 * This will receive a query hook and add localStorage caching functionality to it.
 * Note that this will still call the underlying resource, and automatically update the cache once it gets
 * the new information.
 *
 * This will change the return slightly based on whether the item is in cache or not. It introduces a new key called
 * `errorWhileRevalidating` that will contain the contents of `error` when the item is in cache.
 *
 * This allows the consumer to do something different (e.g. showing a stale data warning) instead of fully erroring out.
 * For more details on how the return changes, refer to the following table:
 *
 * | in cache              | data               | loading  | error     | errorWhileRevalidating | ...rest  |
 * |-----------------------|--------------------| ---------|-----------|------------------------|----------|
 * | yes                   | original || cached | false    | undefined | original               | original |
 * | no (or skipCache)     | original           | original | original  | undefined              | original |
 *
 * @param useQueryHook
 * @param options
 * @returns
 */
export const makeQueryStaleWhileRevalidate =
  <THook extends QueryHook<any, any>>(useQueryHook: THook, options: StaleWhileRevalidateOptions) =>
  (
    queryOptions: Parameters<THook>[0],
    cacheOptions?: {
      skipCache?: boolean
    }
  ): ReturnType<THook> & {
    /**
     * Will contain the error from this request as long as there was an error and
     * this request was cached. This is meant so the normal error can be 'swallowed'
     * while still allowing us to act on it.
     */
    errorWhileRevalidating?: ApolloError
  } => {
    type TData = ExtractQueryData<THook>
    type THookResult = ReturnType<THook> & { errorWhileRevalidating?: ApolloError }

    const cacheKey = useDeepMemo(
      () =>
        `${cacheNamespace}:${cacheVersion}:${options.baseCacheKey}:${JSON.stringify(
          // @ts-expect-error TS18048 — strictNullChecks. TODO: fix strict null check
          queryOptions.variables
        )}`,
      // @ts-ignore, types for useDeepMemo are not right
      [queryOptions.variables]
    )

    // Memoize queryOptions so the underlying Apollo hook receives a stable reference,
    // avoiding unnecessary internal work on re-renders.
    const stableQueryOptions = useDeepMemo(
      () => queryOptions,
      // @ts-ignore, types for useDeepMemo are not right
      [queryOptions]
    )

    const result = useQueryHook(stableQueryOptions)

    const maxCacheAgeMs = options.maxCacheAgeMs ?? DEFAULT_MAX_CACHE_AGE_MS

    // --- Data stabilization ---
    // Apollo often returns new object references for identical data on re-renders.
    // Track data changes by value (via isEqual) and expose a version counter so
    // the cache-write effect only fires when data truly changes.
    const dataVersionRef = useRef(0)
    const stableDataRef = useRef<TData | undefined>(undefined)

    if (result.data && !isEqual(stableDataRef.current, result.data)) {
      stableDataRef.current = result.data
      dataVersionRef.current += 1
    }

    // --- Cold start cache read ---
    // When Apollo has no data yet, read from cache during render so the user
    // sees cached data immediately. readCacheEntry checks the in-memory Map
    // first (O(1)), only falling through to localStorage on the very first
    // access for a given key.
    const cachedEntry = !result.data ? readCacheEntry<TData>(cacheKey) : undefined
    const isExpired = cachedEntry ? Date.now() - cachedEntry.cachedAt > maxCacheAgeMs : false
    const cachedData = isExpired ? undefined : cachedEntry?.data
    const hasValidCache = !isExpired && Boolean(cachedEntry)

    // --- Return data stabilization ---
    // When Apollo responds with data identical to what was already served from
    // cache, keep the original cached reference. This prevents unnecessary
    // re-renders in consuming components whose useMemos depend on the data ref.
    const stableReturnDataRef = useRef<TData | undefined>(undefined)
    const computedReturnData = (result.data || cachedData) as TData | undefined

    if (computedReturnData !== stableReturnDataRef.current) {
      if (!computedReturnData || !isEqual(stableReturnDataRef.current, computedReturnData)) {
        stableReturnDataRef.current = computedReturnData
      }
    }

    // --- Deferred cache write ---
    // Schedules a write in idle time only when data has truly changed (by value).
    // No React state is set — only the in-memory Map and localStorage — so no re-renders.
    const skipCache = cacheOptions?.skipCache
    useEffect(() => {
      if (skipCache) return
      const data = stableDataRef.current
      if (!data || result.error) return

      return scheduleIdle(() => {
        const cached = readCacheEntry<TData>(cacheKey)

        // Skip write if data hasn't changed and cache isn't expired
        if (cached) {
          const isCacheExpired = Date.now() - cached.cachedAt > maxCacheAgeMs
          if (!isCacheExpired && isEqual(cached.data, data)) {
            return
          }
        }

        const entry: CachedEntry<TData> = { data, cachedAt: Date.now() }
        writeCacheEntry(cacheKey, entry, MAX_CACHE_ENTRY_BYTES)
      })
      // dataVersionRef.current is intentionally used as a dep — it's a render-stable
      // proxy for deep data changes, replacing Apollo's unstable result.data reference.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [skipCache, dataVersionRef.current, result.error, cacheKey, maxCacheAgeMs])

    // If cache is not enabled, return results as-is
    if (cacheOptions?.skipCache) return result as THookResult

    return {
      ...result,
      // This will potentially swallow the error, as long as valid (non-expired) cache exists
      error: hasValidCache ? undefined : result.error,
      errorWhileRevalidating: hasValidCache ? result.error : undefined,
      // Return stabilized data — keeps the same reference when Apollo returns
      // data identical to what was already served from cache.
      data: stableReturnDataRef.current,
      // If Apollo already has data, we're done. Otherwise, show cached if valid, else pass through Apollo loading.
      loading: result.data ? false : hasValidCache ? false : result.loading,
    } as THookResult
  }

/**
 * Deletes any SWR cache entries from localStorage whose version doesn't match
 * the current cacheVersion. This cleans up stale entries after a version bump.
 */
export const useQueryStaleWhileRevalidateCleanup = () => {
  useEffect(() => {
    try {
      const keysToDelete: string[] = []

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (!key) continue

        // Only look at keys in our namespace
        if (!key.startsWith(cacheNamespace)) continue

        // Delete if it doesn't match the current version prefix
        if (!key.startsWith(`${cacheNamespace}:${cacheVersion}:`)) {
          keysToDelete.push(key)
          continue
        }

        // Delete if the entry is older than the max cache age
        try {
          const raw = localStorage.getItem(key)
          if (raw) {
            const entry = JSON.parse(raw) as { cachedAt?: number }
            if (!entry.cachedAt || Date.now() - entry.cachedAt > DEFAULT_MAX_CACHE_AGE_MS) {
              keysToDelete.push(key)
            }
          }
        } catch {
          // Unparseable entry — delete it
          keysToDelete.push(key)
        }
      }

      keysToDelete.forEach(key => {
        localStorage.removeItem(key)
        memoryCache.delete(key)
      })

      // Size trigger: version/TTL cleanup above only removes stale entries. If the
      // surviving (fresh) entries still exceed the namespace budget — e.g. an ops
      // user who visited many retailers this session — evict oldest-first until
      // under budget so the namespace never starves the shared quota. The eviction
      // self-gates on a cheap length-only sum, so this is a no-op scan when already
      // under budget.
      const evictedKeys = evictSwrCacheEntriesToBudget(SWR_NAMESPACE_BUDGET_BYTES)
      if (evictedKeys > 0) {
        // Keep the in-memory L1 cache consistent with what was removed from
        // localStorage — drop everything so the next read re-reads survivors.
        memoryCache.clear()
      }
    } catch {
      console.error('Failed to clean up SWR cache keys')
    }
  }, [])
}
